/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import { ChangeEvent, FC, FocusEvent, KeyboardEvent, useCallback, useState } from "react";

import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { v4 as uuid } from "uuid";

import { ShopVariantData, ShopVariantValueData } from "../../types";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Delete, Move } from "@/icons/outlined";

interface ShopVariantEditorProps {
  variant: ShopVariantData;
  onChange: (updatedVariant: ShopVariantData) => void;
  onDelete: () => void;
  errorMessage?: string;
  deleteDisabledTooltip?: string;
  purchasedValueIds?: ReadonlySet<string>;
  valueDeleteDisabledTooltip?: string;
}

const VALUE_INPUT_WIDTH = 168;
const VALUE_INPUT_DEBOUNCE_MS = 150;

export const ShopVariantEditor: FC<ShopVariantEditorProps> = ({
  variant,
  onChange,
  onDelete,
  errorMessage,
  deleteDisabledTooltip,
  purchasedValueIds,
  valueDeleteDisabledTooltip,
}) => {
  const { t } = useTranslate();

  const [draftId, setDraftId] = useState<string>(() => uuid());

  const handleNameChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange({ ...variant, name: event.target.value });
  };

  const handleRowChange = useCallback(
    (rowId: string, name: string) => {
      if (rowId === draftId) {
        if (!name.trim()) {
          return;
        }
        const promotedValue: ShopVariantValueData = {
          id: rowId,
          name,
          sortIndex: variant.values.length,
        };
        onChange({ ...variant, values: [...variant.values, promotedValue] });
        setDraftId(uuid());
        return;
      }

      const updatedValues = variant.values.map((value) => (value.id === rowId ? { ...value, name } : value));
      onChange({ ...variant, values: updatedValues });
    },
    [draftId, onChange, variant]
  );

  const handleRemoveValue = useCallback(
    (rowId: string) => {
      const updatedValues = variant.values
        .filter((v) => v.id !== rowId)
        .map((value, index) => ({ ...value, sortIndex: index }));
      onChange({ ...variant, values: updatedValues });
    },
    [onChange, variant]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = variant.values.findIndex((v) => v.id === active.id);
    const newIndex = variant.values.findIndex((v) => v.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reorderedValues = arrayMove(variant.values, oldIndex, newIndex).map((v, index) => ({
      ...v,
      sortIndex: index,
    }));
    onChange({ ...variant, values: reorderedValues });
  };

  const draftRow: ShopVariantValueData = {
    id: draftId,
    name: "",
    sortIndex: variant.values.length,
  };
  const rows: ShopVariantValueData[] = [...variant.values, draftRow];
  const draftPlaceholder = t(
    "dashboard",
    variant.values.length === 0 ? "ticketing.variant.addValue" : "ticketing.variant.addAnotherValue"
  );
  const valuePlaceholder = t("dashboard", "ticketing.variant.valuePlaceholder");

  return (
    <Stack
      gap={1}
      sx={(theme) => ({
        padding: 2,
        border: `1px solid ${errorMessage ? theme.palette.border.danger.intense : theme.palette.border.neutral.quiet}`,
        borderRadius: 1,
      })}
    >
      <Stack direction="row" alignItems="flex-end" gap={1}>
        <Box sx={{ flex: 1 }}>
          <DashboardTextField
            label={t("dashboard", "ticketing.variant.name")}
            tooltipText={t("dashboard", "ticketing.variant.nameTooltip")}
            value={variant.name}
            onChange={handleNameChange}
            placeholder={t("dashboard", "ticketing.variant.namePlaceholder")}
          />
        </Box>
        <DashboardTooltip title={deleteDisabledTooltip} disabled={!deleteDisabledTooltip}>
          <Box component="span">
            <DashboardIconButton
              onClick={onDelete}
              vibe="neutral"
              size="small"
              variant="text"
              disabled={Boolean(deleteDisabledTooltip)}
              data-test="shop-variant-delete-button"
            >
              <Delete />
            </DashboardIconButton>
          </Box>
        </DashboardTooltip>
      </Stack>

      <Stack gap={0.5}>
        <DashboardTypography variant="body2">{t("dashboard", "ticketing.variant.values")}</DashboardTypography>

        {errorMessage ? (
          <DashboardTypography variant="caption" sx={(theme) => ({ color: theme.palette.text.danger.intense })}>
            {errorMessage}
          </DashboardTypography>
        ) : null}

        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={variant.values.map((v) => v.id)} strategy={verticalListSortingStrategy}>
            <Stack gap={0.5}>
              {rows.map((value) => {
                const isDraft = value.id === draftId;
                const isPurchasedValue = Boolean(purchasedValueIds?.has(value.id));
                return (
                  <SortableValueRow
                    key={value.id}
                    value={value}
                    isDraft={isDraft}
                    placeholder={isDraft ? draftPlaceholder : valuePlaceholder}
                    onNameChange={handleRowChange}
                    onRemove={handleRemoveValue}
                    deleteDisabledTooltip={isPurchasedValue ? valueDeleteDisabledTooltip : undefined}
                  />
                );
              })}
            </Stack>
          </SortableContext>
        </DndContext>
      </Stack>
    </Stack>
  );
};

interface SortableValueRowProps {
  value: ShopVariantValueData;
  isDraft: boolean;
  placeholder: string;
  onNameChange: (id: string, name: string) => void;
  onRemove: (id: string) => void;
  deleteDisabledTooltip?: string;
}

const ACTIONS_CLASS = "shop-variant-value-actions";

const SortableValueRow: FC<SortableValueRowProps> = ({
  value,
  isDraft,
  placeholder,
  onNameChange,
  onRemove,
  deleteDisabledTooltip,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: value.id,
    disabled: isDraft,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onNameChange(value.id, event.target.value);
  };

  const rowSx = {
    [`& .${ACTIONS_CLASS}`]: {
      visibility: isDraft ? "hidden" : "visible",
    },
    [`&:focus-within .${ACTIONS_CLASS}`]: {
      visibility: "hidden",
    },
  };

  return (
    <Stack ref={setNodeRef} style={style} direction="row" gap={0.5} alignItems="center" sx={rowSx}>
      <Box className={ACTIONS_CLASS}>
        <DashboardIconButton
          {...attributes}
          {...listeners}
          vibe="neutral"
          size="small"
          variant="text"
          sx={{ cursor: isDragging ? "grabbing" : "grab" }}
        >
          <Move />
        </DashboardIconButton>
      </Box>
      <Box sx={{ width: VALUE_INPUT_WIDTH }}>
        <DashboardTextField
          defaultValue={value.name}
          debounced={VALUE_INPUT_DEBOUNCE_MS}
          onChange={(event) => onNameChange(value.id, event.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
      </Box>
      <Box className={ACTIONS_CLASS}>
        <DashboardTooltip title={deleteDisabledTooltip} disabled={!deleteDisabledTooltip}>
          <Box component="span">
            <DashboardIconButton
              onClick={() => onRemove(value.id)}
              vibe="neutral"
              size="small"
              variant="text"
              disabled={Boolean(deleteDisabledTooltip)}
              data-test="shop-variant-value-delete-button"
            >
              <Delete />
            </DashboardIconButton>
          </Box>
        </DashboardTooltip>
      </Box>
    </Stack>
  );
};
